import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import {
  completeStoppedTraversal,
  setFormGuard,
} from '@/lib/history-interceptor';

export default function useFormGuardController() {
  const guards = useRef(new Map());
  const skipGuard = useRef(false);
  const [pendingVisit, setPendingVisit] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCopy, setDialogCopy] = useState(null);

  const register = useCallback((id, isDirtyFn) => {
    guards.current.set(id, isDirtyFn);
  }, []);

  const unregister = useCallback((id) => {
    guards.current.delete(id);
  }, []);

  const activeGuard = useCallback(() => {
    for (const fn of guards.current.values()) {
      const result = fn();
      if (result) return result;
    }
    return false;
  }, []);

  // Intercept Inertia navigation
  useEffect(() => {
    function handleBefore(event) {
      if (skipGuard.current) return;

      const visit = event.detail.visit;

      // Only block GET navigations (page changes), not form submissions
      if (visit.method !== 'get') return;

      // Don't block partial reloads (router.reload())
      if (visit.preserveState) return;

      const guard = activeGuard();
      if (!guard) return;

      event.preventDefault();
      setPendingVisit(visit);
      setDialogCopy(typeof guard === 'object' ? guard : null);
      setDialogOpen(true);
    }

    document.addEventListener('inertia:before', handleBefore);
    return () => document.removeEventListener('inertia:before', handleBefore);
  }, [activeGuard]);

  // Browser Back and Forward, the iOS swipe and Android's Back button.
  // popstate can't be cancelled, and Inertia's own listener would restore the
  // previous page under the dialog (#524), so lib/history-interceptor.js stops
  // the traversal, steps back to the form, and then this asks.
  useEffect(
    () =>
      setFormGuard({
        isDirty: activeGuard,
        onBlocked: () => {
          const guard = activeGuard();
          if (!guard) return;
          setPendingVisit('__popstate__');
          setDialogCopy(typeof guard === 'object' ? guard : null);
          setDialogOpen(true);
        },
        // The step back to the form never arrived (a jump the browser
        // couldn't report): show the page the address belongs to.
        onLost: () => {
          skipGuard.current = true;
          router.visit(window.location.href, { replace: true });
          skipGuard.current = false;
        },
      }),
    [activeGuard],
  );

  // Native beforeunload for tab close / browser refresh
  useEffect(() => {
    function handleBeforeUnload(e) {
      if (activeGuard()) e.preventDefault();
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeGuard]);

  function handleDiscard() {
    setDialogOpen(false);
    if (!pendingVisit) return;

    if (pendingVisit === '__popstate__') {
      completeStoppedTraversal();
    } else {
      skipGuard.current = true;
      const { url, ...options } = pendingVisit;
      router.visit(url, options);
      // Reset bypass after the synchronous before event fires
      skipGuard.current = false;
    }
    setPendingVisit(null);
    setDialogCopy(null);
  }

  function handleKeepEditing() {
    setDialogOpen(false);
    setPendingVisit(null);
    setDialogCopy(null);
  }

  const registration = useMemo(
    () => ({ register, unregister }),
    [register, unregister],
  );
  return {
    registration,
    dialogOpen,
    dialogCopy,
    setDialogOpen,
    handleDiscard,
    handleKeepEditing,
  };
}
